import React, { useState } from 'react';
import { Search, Copy, Check, AlertCircle, CheckCircle2, LoaderCircle, Zap } from 'lucide-react';
import { apiFetch } from '../services/api';
import './ConsultaUC.css';

const somenteDigitos = (valor) => String(valor || '').replace(/\D/g, '');
const formatarCpf = (valor) => {
  const d = somenteDigitos(valor).slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
};
const mostrar = (valor) => {
  if (valor === null || valor === undefined || String(valor).trim() === '') return 'N/A';
  return String(valor).trim();
};
const booleanoSimNao = (valor) => {
  const v = String(valor ?? '').trim().toLowerCase();
  if (['true', '1', 'sim', 's'].includes(v)) return 'Sim';
  if (['false', '0', 'não', 'nao', 'n'].includes(v)) return 'Não';
  return 'N/A';
};
const combineEndereco = (end) => {
  const a = String(end.Endereco || '').trim();
  const b = String(end.Rua || '').trim();
  if (!a) return b;
  if (!b) return a;
  const palavrasA = a.split(/\s+/);
  const palavrasB = b.split(/\s+/);
  const normal = (v) => v.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '');
  while (palavrasA.length && palavrasB.length && normal(palavrasA[palavrasA.length - 1]) === normal(palavrasB[0])) palavrasB.shift();
  return [palavrasA.join(' '), palavrasB.join(' ')].filter(Boolean).join(' ');
};
const formatarGrupo = (valor) => {
  const v = String(valor ?? '').trim().toLowerCase();
  if (['true', '1', 'sim', 's'].includes(v)) return 'Alta tensão (Grupo A)';
  if (['false', '0', 'não', 'nao', 'n'].includes(v)) return 'Baixa tensão';
  return 'N/A';
};
const valorBaixaRenda = (obj) => {
  if (!obj?.sucesso) return 'Indisponível';
  if (obj.baixaRendaTexto) {
    const texto = String(obj.baixaRendaTexto).trim().toLowerCase();
    if (['sim', 'true', 's', '1'].includes(texto)) return 'Sim';
    if (['não', 'nao', 'false', 'n', '0'].includes(texto)) return 'Não';
    return obj.baixaRendaTexto;
  }
  if (typeof obj.baixaRenda === 'boolean') return obj.baixaRenda ? 'Sim' : 'Não';
  return 'N/A';
};
const valorParcelamento = (obj) => {
  if (!obj?.sucesso) return 'Indisponível';
  return obj.existeParcelamento ? 'Sim' : 'Não';
};

function copiarTexto(texto) {
  if (navigator.clipboard?.writeText) return navigator.clipboard.writeText(texto);
  const area = document.createElement('textarea');
  area.value = texto;
  area.style.position = 'fixed';
  area.style.opacity = '0';
  document.body.appendChild(area);
  area.select();
  const ok = document.execCommand('copy');
  area.remove();
  return ok ? Promise.resolve() : Promise.reject(new Error('Não foi possível copiar'));
}

function ConsultaUC() {
  const [cpf, setCpf] = useState('');
  const [enderecos, setEnderecos] = useState([]);
  const [resultado, setResultado] = useState(null);
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [copiado, setCopiado] = useState(-1);

  const consultar = async (event) => {
    event.preventDefault();
    const cpfLimpo = somenteDigitos(cpf);
    if (cpfLimpo.length !== 11) {
      setErro('Digite um CPF com 11 números.');
      setEnderecos([]);
      setResultado(null);
      return;
    }
    setCarregando(true);
    setErro('');
    setEnderecos([]);
    setResultado(null);
    try {
      const dados = await apiFetch(`/cpfl/consultar-cpf-detalhado?cpf=${encodeURIComponent(cpfLimpo)}`);
      setResultado(dados);
      if (!dados?.sucesso) {
        setErro(dados?.mensagem || 'A consulta não foi concluída.');
        return;
      }
      setEnderecos(Array.isArray(dados.enderecos) ? dados.enderecos : []);
    } catch (e) {
      setErro(e.message || 'Erro ao consultar a CPFL. Tente novamente.');
    } finally {
      setCarregando(false);
    }
  };

  const copiar = async (end, indice) => {
    const endereco = combineEndereco(end);
    const baixa = valorBaixaRenda(end.BaixaRendaCPFL);
    const parcelas = valorParcelamento(end.ParcelamentoCPFL);
    const texto = [
      `Nome: ${mostrar(end.Nome)}`,
      `CPF: ${formatarCpf(cpf)}`,
      `UC: ${mostrar(end.Uc)}`,
      `Nova UC: ${mostrar(end.NovaUC)}`,
      `PN: ${mostrar(end.Parceiro)}`,
      `Endereço: ${mostrar(endereco)}${end.NumImovel ? `, ${end.NumImovel}` : ''}`,
      `Complemento: ${mostrar(end.Complemento)}`,
      `Bairro: ${mostrar(end.Bairro)}`,
      `Cidade/UF: ${mostrar(end.Cidade)}/${mostrar(end.UF)}`,
      `CEP: ${mostrar(end.CEP)}`,
      `Situação: ${mostrar(end.StatusCPFL || end.SituacaoInstalacao)}`,
      `Débitos em aberto: ${booleanoSimNao(end.temDebitos)}`,
      `Grupo: ${formatarGrupo(end.GrupoA)}`,
      `Baixa Renda: ${baixa}`,
      `Parcelamento: ${parcelas}`,
    ].join('\n');
    try {
      await copiarTexto(texto);
      setCopiado(indice);
      window.setTimeout(() => setCopiado((atual) => atual === indice ? -1 : atual), 1800);
    } catch (e) {
      setErro('O navegador não permitiu copiar os dados.');
    }
  };

  const cpfFormatado = formatarCpf(cpf);
  return (
    <section className="consulta-uc-page">
      <header className="consulta-uc-heading">
        <div className="consulta-uc-heading-icon"><Zap size={20} /></div>
        <div>
          <h1>Consulta de UC</h1>
          <p>Digite o CPF para buscar as instalações vinculadas.</p>
        </div>
      </header>

      <form className="consulta-uc-search" onSubmit={consultar}>
        <label htmlFor="consulta-uc-cpf">CPF</label>
        <div className="consulta-uc-search-row">
          <input
            id="consulta-uc-cpf"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            placeholder="000.000.000-00"
            maxLength={14}
            value={cpfFormatado}
            onChange={(event) => setCpf(formatarCpf(event.target.value))}
          />
          <button type="submit" disabled={carregando}>
            {carregando ? <LoaderCircle className="consulta-uc-spinner" size={17} /> : <Search size={17} />}
            {carregando ? 'Consultando...' : 'Consultar'}
          </button>
        </div>
      </form>

      {erro && <div className="consulta-uc-alert error"><AlertCircle size={18} /><span>{erro}</span></div>}
      {resultado?.sucesso && !erro && (
        <div className="consulta-uc-alert success"><CheckCircle2 size={18} /><span>Sucesso! Total de instalações encontradas: {enderecos.length}</span></div>
      )}
      {resultado?.sucesso && enderecos.length === 0 && !erro && (
        <div className="consulta-uc-empty">Nenhuma instalação vinculada a este CPF foi encontrada.</div>
      )}

      <div className="consulta-uc-cards">
        {enderecos.map((end, indice) => {
          const situacao = end.StatusCPFL || end.SituacaoInstalacao;
          const logradouro = combineEndereco(end);
          const statusClasse = String(situacao || '').toLowerCase() === 'ativa' ? 'is-active' : '';
          const baixa = valorBaixaRenda(end.BaixaRendaCPFL);
          const parcelamento = valorParcelamento(end.ParcelamentoCPFL);
          const detalhesParcelamento = end.ParcelamentoCPFL?.sucesso && end.ParcelamentoCPFL?.existeParcelamento
            ? ` (${end.ParcelamentoCPFL.qtdTotal || 0}; em aberto: ${end.ParcelamentoCPFL.qtdEmAberto || 0})`
            : '';
          return (
            <article className="consulta-uc-card" key={`${end.Uc || 'uc'}-${indice}`}>
              <button className="consulta-uc-copy" type="button" onClick={() => copiar(end, indice)}>
                {copiado === indice ? <Check size={15} /> : <Copy size={15} />}
                {copiado === indice ? 'Copiado' : 'Copiar'}
              </button>
              <div className="consulta-uc-fields">
                <Campo rotulo="Nome" valor={mostrar(end.Nome)} />
                <Campo rotulo="CPF" valor={cpfFormatado || 'N/A'} />
                <Campo rotulo="UC" valor={mostrar(end.Uc)} />
                <Campo rotulo="Nova UC" valor={mostrar(end.NovaUC)} destaque />
                <Campo rotulo="PN" valor={mostrar(end.Parceiro)} />
                <Campo rotulo="Endereço" valor={`${mostrar(logradouro)}${end.NumImovel ? `, ${end.NumImovel}` : ''}`} />
                <Campo rotulo="Complemento" valor={mostrar(end.Complemento)} />
                <Campo rotulo="Bairro" valor={mostrar(end.Bairro)} />
                <Campo rotulo="Cidade/UF" valor={`${mostrar(end.Cidade)}/${mostrar(end.UF)}`} />
                <Campo rotulo="CEP" valor={mostrar(end.CEP)} />
                <Campo rotulo="Situação" valor={mostrar(situacao)} className={statusClasse} />
                <Campo rotulo="Débitos em aberto" valor={booleanoSimNao(end.temDebitos)} />
                <Campo rotulo="Grupo" valor={formatarGrupo(end.GrupoA)} />
                <Campo rotulo="Baixa Renda" valor={baixa} />
                <Campo rotulo="Parcelamento" valor={`${parcelamento}${detalhesParcelamento}`} />
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function Campo({ rotulo, valor, destaque = false, className = '' }) {
  return (
    <div className="consulta-uc-field">
      <strong>{rotulo}:</strong>
      <span className={`${destaque ? 'is-highlight ' : ''}${className}`.trim()}>{valor}</span>
    </div>
  );
}

export default ConsultaUC;
